import constants from '@playcastdotio/GlobalConfig';

const ensureTrailingSlash = (url: string) => (url.endsWith('/') ? url : `${url}/`);

const handoffBaseUrl = () => `${ensureTrailingSlash(constants.REALTIME_API_BASE_URL)}realtime/campaigns/handoff`;

/**
 * Token resolution is awaited during quickstart init, so an unresponsive API would hold
 * the visitor on a blank page. Losing the handoff identity is far cheaper than that: they
 * simply continue as a new visitor.
 */
const TOKEN_RESOLVE_TIMEOUT_MS = 4000;

/**
 * Longer than the token deadline, since this call waits on an upstream mail provider and
 * the visitor is watching a spinner rather than a blank page. Bounded all the same: an
 * answerless request would otherwise strand the form mid-submit with nothing to retry.
 */
const EMAIL_SEND_TIMEOUT_MS = 12000;

const CHALLENGE_TIMEOUT_MS = 8000;

/** Runs `request` under a deadline that spans the whole exchange, body reads included. */
const withDeadline = async <T>(timeoutMs: number, request: (signal: AbortSignal) => Promise<T>): Promise<T> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await request(controller.signal);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export interface SendHandoffEmailInput {
  email: string;
  /** Path slug of the campaign. The backend builds the emailed link from this. */
  urlSlug: string;
  persistentId: string;
  /** One-time challenge from mintHandoffChallenge. */
  challenge: string;
  /** UTM and dynamic params to carry over. The backend re-encodes them and appends its own token. */
  query?: string;
  /** Device class this visitor is leaving — stamped on the mailed URL for funnel join. */
  originDeviceType?: 'ios' | 'android' | 'desktop';
}

export type SendHandoffEmailResult =
  | { ok: true }
  | { ok: false; reason: 'invalid_email' | 'rate_limited' | 'invalid_challenge' | 'error' };

/**
 * Mint a one-time challenge required before sendHandoffEmail. Call when the mobile
 * fallback screen mounts so a cold POST without a player page load cannot send mail.
 */
export const mintHandoffChallenge = async (urlSlug: string): Promise<string | null> => {
  try {
    return await withDeadline(CHALLENGE_TIMEOUT_MS, async (signal) => {
      const response = await fetch(`${handoffBaseUrl()}/challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urlSlug }),
        signal,
      });
      if (!response.ok) return null;
      const data = await response.json();
      const challenge = data?.data?.challenge;
      return typeof challenge === 'string' && challenge.length > 0 ? challenge : null;
    });
  } catch {
    return null;
  }
};

/**
 * Ask the backend to email a campaign link to the visitor.
 *
 * Only the slug is sent, never a full URL — the endpoint is unauthenticated, so the link
 * it mails is assembled server-side from trusted parts.
 */
export const sendHandoffEmail = async (input: SendHandoffEmailInput): Promise<SendHandoffEmailResult> => {
  try {
    return await withDeadline(EMAIL_SEND_TIMEOUT_MS, async (signal) => {
      const response = await fetch(`${handoffBaseUrl()}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        signal,
      });

      if (response.ok) return { ok: true } as const;
      if (response.status === 429) return { ok: false, reason: 'rate_limited' } as const;

      if (response.status === 400) {
        const body = await response.json().catch(() => null);
        if (body?.code === 'invalid_email') return { ok: false, reason: 'invalid_email' } as const;
        if (body?.code === 'invalid_challenge') return { ok: false, reason: 'invalid_challenge' } as const;
        return { ok: false, reason: 'error' } as const;
      }

      return { ok: false, reason: 'error' } as const;
    });
  } catch {
    return { ok: false, reason: 'error' };
  }
};

/**
 * Exchange an emailed handoff token for the persistent ID it was minted against.
 * Returns null for unknown or expired tokens, in which case the visitor is simply
 * treated as new.
 */
export const resolveHandoffToken = async (token: string): Promise<string | null> => {
  try {
    return await withDeadline(TOKEN_RESOLVE_TIMEOUT_MS, async (signal) => {
      const response = await fetch(`${handoffBaseUrl()}/${encodeURIComponent(token)}`, { signal });
      if (!response.ok) return null;
      const data = await response.json();
      const persistentId = data?.data?.persistentId;
      return typeof persistentId === 'string' && persistentId.startsWith('player_') ? persistentId : null;
    });
  } catch {
    return null;
  }
};
